import { useState, useEffect, useCallback } from 'react';

const SUPABASE_URL = 'https://fjquhegucjotzmfmujrv.supabase.co';
const ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZqcXVoZWd1Y2pvdHptZm11anJ2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI1ODQ1ODgsImV4cCI6MjA4ODE2MDU4OH0.f-L47ajh0dfR7FwwWeWmWT6ChzaG6-LjJfK4bzAM0vc';

type Status = 'idle' | 'sending' | 'success' | 'error';

export default function ContactModal() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<Status>('idle');

  useEffect(() => {
    const handler = () => setOpen(true);
    document.addEventListener('open-contact-modal', handler);
    return () => document.removeEventListener('open-contact-modal', handler);
  }, []);

  const reset = useCallback(() => {
    setName('');
    setEmail('');
    setSubject('');
    setMessage('');
    setStatus('idle');
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setTimeout(reset, 300);
  }, [reset]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/contact_submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': ANON_KEY,
          'Authorization': `Bearer ${ANON_KEY}`,
          'Prefer': 'return=minimal',
        },
        body: JSON.stringify({ name: name || '', email, subject, message, status: 'new' }),
      });
      if (!res.ok) throw new Error('Failed');
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }, [name, email, subject, message]);

  if (!open) return null;

  return (
    <div style={overlay} onClick={close}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <div style={header}>
          <span style={{ fontSize: 15, fontWeight: 700 }}>お問い合わせ</span>
          <button onClick={close} style={closeBtn}>✕</button>
        </div>

        {status === 'success' ? (
          <div style={{ padding: '40px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: 40, color: '#10b981', marginBottom: 12 }}>✓</div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>送信完了</div>
            <p style={{ fontSize: 13, color: '#a0aec0', lineHeight: 1.5, marginBottom: 20 }}>
              お問い合わせを受け付けました。<br />内容を確認の上、ご返信いたします。
            </p>
            <button onClick={close} style={secondaryBtn}>閉じる</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ padding: '16px 24px 20px' }}>
            <p style={{ fontSize: 13, color: '#a0aec0', marginBottom: 16, lineHeight: 1.5 }}>
              ご質問・ご要望・不具合報告など、お気軽にお問い合わせください。
            </p>

            <div style={field}>
              <label style={label}>お名前<span style={optional}>（任意）</span></label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="お名前" style={input} />
            </div>
            <div style={field}>
              <label style={label}>メールアドレス<span style={required}>必須</span></label>
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" style={input} />
            </div>
            <div style={field}>
              <label style={label}>件名<span style={required}>必須</span></label>
              <input type="text" required value={subject} onChange={e => setSubject(e.target.value)} placeholder="件名を入力してください" style={input} />
            </div>
            <div style={field}>
              <label style={label}>本文<span style={required}>必須</span></label>
              <textarea required value={message} onChange={e => setMessage(e.target.value)} placeholder="お問い合わせ内容を入力してください" rows={5} style={textarea} />
            </div>

            {status === 'error' && (
              <div style={errorBox}>送信に失敗しました。しばらくしてから再度お試しください。</div>
            )}

            <button type="submit" disabled={status === 'sending'} style={submitBtn}>
              {status === 'sending' ? '送信中...' : '送信'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  overflowX: 'hidden', boxSizing: 'border-box',
};
const modal: React.CSSProperties = {
  background: '#1a1a1a', borderRadius: 12, width: '100%', maxWidth: 460,
  maxHeight: '90vh', overflowY: 'auto', overflowX: 'hidden',
  border: '1px solid #333', color: '#e4e8f0', boxSizing: 'border-box',
};
const header: React.CSSProperties = {
  padding: '12px 24px', borderBottom: '1px solid #333',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
};
const closeBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: '#a0aec0', fontSize: 18, cursor: 'pointer', padding: '4px 8px',
};
const field: React.CSSProperties = { marginBottom: 12 };
const label: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#a0aec0', marginBottom: 4 };
const required: React.CSSProperties = { color: '#f56565', fontSize: 10, marginLeft: 4 };
const optional: React.CSSProperties = { color: '#718096', fontSize: 10, marginLeft: 4 };
const input: React.CSSProperties = {
  width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #333',
  background: '#0d0d0d', color: '#e4e8f0', fontSize: 13, fontFamily: 'inherit', outline: 'none',
};
const textarea: React.CSSProperties = {
  ...input, resize: 'none', lineHeight: 1.5, height: 100,
};
const submitBtn: React.CSSProperties = {
  width: '100%', padding: 10, borderRadius: 6, border: 'none',
  background: '#01a1ff', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 4,
};
const secondaryBtn: React.CSSProperties = {
  padding: '8px 20px', borderRadius: 6, border: '1px solid #333',
  background: '#1a1a1a', color: '#a0aec0', fontSize: 13, cursor: 'pointer',
};
const errorBox: React.CSSProperties = {
  padding: 10, borderRadius: 6, background: '#7f1d1d33', color: '#fca5a5',
  border: '1px solid #7f1d1d', fontSize: 12, marginBottom: 12,
};
